import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { PageHeader, PageBody, StatusBadge, EmptyState, formatMoney } from "../components/Shared";
import { Link } from "react-router-dom";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { FileSpreadsheet, Ship } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";

const ITEM_STATUSES = ["all", "pending", "ordered", "shipped", "delivered", "shipped_to_customer", "cancelled"];
const ORDER_STATUSES = ["all", "open", "in_progress", "completed", "cancelled"];

export default function OrderItems() {
  const [rows, setRows] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [filters, setFilters] = useState({ supplier_id: "all", customer_id: "all", order_status: "all", item_status: "all" });
  const [q, setQ] = useState("");
  const [sel, setSel] = useState({});
  const [shipOpen, setShipOpen] = useState(false);

  const buildParams = () => {
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v && v !== "all") params[k] = v; });
    return params;
  };

  const load = () => api.get("/order-items", { params: buildParams() }).then((r) => setRows(r.data));

  useEffect(() => {
    api.get("/suppliers").then((r) => setSuppliers(r.data));
    api.get("/customers").then((r) => setCustomers(r.data));
  }, []);
  useEffect(() => { load(); }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    if (!q) return rows;
    const s = q.toLowerCase();
    return rows.filter((r) =>
      r.item.part_number?.toLowerCase().includes(s) ||
      r.item.manufacturer?.toLowerCase().includes(s) ||
      r.order_number?.toLowerCase().includes(s) ||
      r.customer_name?.toLowerCase().includes(s)
    );
  }, [rows, q]);

  const selCount = Object.values(sel).filter(Boolean).length;
  const allVisibleSelected = filtered.length > 0 && filtered.every((r) => sel[`${r.order_id}|${r.item.id}`]);
  const toggleAllVisible = () => {
    setSel((s) => {
      const next = { ...s };
      for (const r of filtered) {
        const k = `${r.order_id}|${r.item.id}`;
        if (allVisibleSelected) delete next[k];
        else next[k] = true;
      }
      return next;
    });
  };

  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  const exportXlsx = async () => {
    try {
      const params = buildParams();
      if (q) params.q = q;
      const res = await api.get("/order-items.xlsx", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      a.href = url;
      a.download = `order-items-${stamp}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${filtered.length} row${filtered.length === 1 ? "" : "s"}`);
    } catch { toast.error("Export failed"); }
  };

  return (
    <>
      <PageHeader
        title="Order Items"
        subtitle="All items across all orders"
        testid="order-items-title"
        actions={
          <div className="flex items-center gap-2">
            <Button
              onClick={() => setShipOpen(true)}
              disabled={!selCount}
              className="rounded-none bg-blue-600 hover:bg-blue-700"
              data-testid="add-to-shipment-btn"
              title="Seçili kalemleri bir yüklemeye ekle"
            >
              <Ship className="w-4 h-4 mr-2" /> Add to shipment {selCount ? `(${selCount})` : ""}
            </Button>
            <Button
              onClick={exportXlsx}
              variant="outline"
              className="rounded-none border-slate-300 hover:bg-slate-100"
              data-testid="export-order-items-xlsx"
              disabled={filtered.length === 0}
              title="Download current filtered rows as Excel"
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" /> Export Excel ({filtered.length})
            </Button>
          </div>
        }
      />
      <PageBody>
        <div className="grid md:grid-cols-5 gap-3 mb-4 items-end">
          <div>
            <Label className="label-caps">Search</Label>
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Part #, order, customer…" className="rounded-none mt-1" data-testid="items-search" />
          </div>
          <div>
            <Label className="label-caps">Supplier</Label>
            <Select value={filters.supplier_id} onValueChange={(v) => setF("supplier_id", v)}>
              <SelectTrigger className="rounded-none mt-1" data-testid="filter-supplier"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="all">All</SelectItem>
                {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-caps">Customer</Label>
            <Select value={filters.customer_id} onValueChange={(v) => setF("customer_id", v)}>
              <SelectTrigger className="rounded-none mt-1" data-testid="filter-customer"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="all">All</SelectItem>
                {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-caps">Order status</Label>
            <Select value={filters.order_status} onValueChange={(v) => setF("order_status", v)}>
              <SelectTrigger className="rounded-none mt-1" data-testid="filter-order-status"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-none">{ORDER_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-caps">Item status</Label>
            <Select value={filters.item_status} onValueChange={(v) => setF("item_status", v)}>
              <SelectTrigger className="rounded-none mt-1" data-testid="filter-item-status"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-none">{ITEM_STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        {filtered.length === 0 ? <EmptyState title="No items" hint="No order items match the current filters." /> :
          <div className="bg-white border border-slate-300 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-300"><tr className="text-left">
                <th className="py-2 px-3 w-8">
                  <input type="checkbox" checked={allVisibleSelected} onChange={toggleAllVisible} className="w-4 h-4" data-testid="select-all-order-items" />
                </th>
                <th className="py-2 px-3 label-caps">Order</th>
                <th className="py-2 px-3 label-caps">Customer</th>
                <th className="py-2 px-3 label-caps">Part #</th>
                <th className="py-2 px-3 label-caps">Mfr</th>
                <th className="py-2 px-3 label-caps text-right">Qty</th>
                <th className="py-2 px-3 label-caps text-right">Sale</th>
                <th className="py-2 px-3 label-caps">Supplier</th>
                <th className="py-2 px-3 label-caps">Sub-supplier</th>
                <th className="py-2 px-3 label-caps">Item Status</th>
              </tr></thead>
              <tbody>
                {filtered.map((r, i) => (
                  <tr key={`${r.order_id}-${r.item.id}`} className="border-b border-slate-200 hover:bg-slate-50 stagger-row">
                    <td className="py-2 px-3">
                      <input
                        type="checkbox"
                        checked={!!sel[`${r.order_id}|${r.item.id}`]}
                        onChange={() => setSel((s) => ({ ...s, [`${r.order_id}|${r.item.id}`]: !s[`${r.order_id}|${r.item.id}`] }))}
                        className="w-4 h-4"
                        data-testid={`select-item-${r.item.id}`}
                      />
                    </td>
                    <td className="py-2 px-3 font-mono"><Link to={`/orders/${r.order_id}`} className="text-blue-700 hover:text-blue-900">{r.order_number}</Link></td>
                    <td className="py-2 px-3">{r.customer_name}</td>
                    <td className="py-2 px-3 font-mono">{r.item.part_number}</td>
                    <td className="py-2 px-3">{r.item.manufacturer || "-"}</td>
                    <td className="py-2 px-3 font-mono text-right">{r.item.quantity}</td>
                    <td className="py-2 px-3 font-mono text-right">{formatMoney(r.item.unit_price, r.item.currency)}</td>
                    <td className="py-2 px-3">{r.supplier_name || <span className="text-slate-400">—</span>}</td>
                    <td className="py-2 px-3">{r.sub_supplier_name || <span className="text-slate-400">—</span>}</td>
                    <td className="py-2 px-3"><StatusBadge status={r.item.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        }
      </PageBody>

      <AddToShipmentDialog
        open={shipOpen}
        onClose={() => { setShipOpen(false); setSel({}); load(); }}
        refs={Object.keys(sel).filter((k) => sel[k]).map((k) => {
          const [order_id, item_id] = k.split("|");
          return { order_id, item_id };
        })}
      />
    </>
  );
}

function AddToShipmentDialog({ open, onClose, refs }) {
  const [shipments, setShipments] = useState([]);
  const [target, setTarget] = useState("new");
  const [existingId, setExistingId] = useState("");
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTarget("new"); setNewName(""); setExistingId("");
    api.get("/shipments").then((r) => setShipments(r.data));
  }, [open]);

  const submit = async () => {
    if (!refs.length) return toast.error("Seçili kalem yok");
    setBusy(true);
    try {
      let added = 0;
      let errors = [];
      if (target === "new") {
        if (!newName.trim()) { toast.error("Yükleme adı gerekli"); setBusy(false); return; }
        const { data } = await api.post("/shipments", { name: newName.trim(), items: refs });
        added = data.shipment?.items?.length ?? 0;
        errors = data.errors || [];
      } else {
        if (!existingId) { toast.error("Yükleme seçin"); setBusy(false); return; }
        const { data } = await api.post(`/shipments/${existingId}/items`, { items: refs });
        added = data.added || 0;
        errors = data.errors || [];
      }
      toast.success(`${added} kalem yükleme altına alındı`);
      errors.slice(0, 3).forEach((e) => toast.warning(e, { duration: 6000 }));
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eklenemedi");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="rounded-none max-w-md" data-testid="add-to-shipment-dialog">
        <DialogHeader><DialogTitle className="font-display tracking-tight flex items-center gap-2"><Ship className="w-5 h-5 text-blue-700" /> Yüklemeye ekle</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="text-sm text-slate-600"><span className="font-mono">{refs.length}</span> kalem seçildi.</div>
          <div>
            <Label className="label-caps">Hedef</Label>
            <Select value={target} onValueChange={setTarget}>
              <SelectTrigger className="rounded-none mt-1" data-testid="shipment-target"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="new" data-testid="shipment-target-new">Yeni yükleme oluştur</SelectItem>
                <SelectItem value="existing" data-testid="shipment-target-existing">Mevcut yükleme</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {target === "new" ? (
            <div>
              <Label className="label-caps">Yükleme adı *</Label>
              <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="örn. Konteyner EK-2026-04" className="rounded-none mt-1" data-testid="shipment-new-name" autoFocus />
            </div>
          ) : (
            <div>
              <Label className="label-caps">Yükleme *</Label>
              <Select value={existingId} onValueChange={setExistingId}>
                <SelectTrigger className="rounded-none mt-1" data-testid="shipment-existing"><SelectValue placeholder="Seçin…" /></SelectTrigger>
                <SelectContent className="rounded-none max-h-60">
                  {shipments.map((s) => <SelectItem key={s.id} value={s.id} data-testid={`shipment-opt-${s.id}`}>{s.name} · {s.item_count} kalem</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-none" data-testid="add-to-shipment-cancel">Vazgeç</Button>
          <Button onClick={submit} disabled={busy} className="rounded-none bg-blue-600 hover:bg-blue-700" data-testid="add-to-shipment-submit">
            <Ship className="w-4 h-4 mr-2" /> {busy ? "Ekleniyor…" : "Ekle"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
